import { io, Socket } from 'socket.io-client';
import { Message, Ticket } from '../types';

export interface SocketEvents {
  // Incoming events
  'new_message': (data: { message: Message }) => void;
  'ticket_updated': (ticket: Ticket) => void;
  'ticket_deleted': (data: { ticketId: string; deletedBy: string; deletedAt: string }) => void;
  'ticket_claimed': (data: { ticketId: string; agent: any; claimedAt: string; handoffFromAI?: boolean; autoClaimed?: boolean; autoAssignedViaMessage?: boolean }) => void;
  'ticket_assigned': (data: { ticketId: string; agentId: string; agentName?: string; assignedAt: string; previousAgent?: string }) => void;
  'ticket_status_changed': (data: { ticketId: string; status: string; assignedAgentId?: string; changedBy?: string; reason?: string }) => void;
  'ai_to_human_handoff': (data: { ticketId: string; previousAgent: string; newAgent: any; handoffAt: string }) => void;
  'agent_joined': (data: { ticketId: string; agentName: string }) => void;
  'agent_left': (data: { ticketId: string; agentName: string }) => void;
  'user_typing': (data: { ticketId: string; isTyping: boolean }) => void;
  'new_ticket': (data: { ticket: any }) => void;
  'ticket_joined': (data: { ticketId: string; success: boolean }) => void;
  'customer_status_changed': (data: { ticketId: string; isOnline: boolean; lastSeen: string }) => void;
  'agent_status_changed': (data: { agentId: string; isOnline: boolean; lastSeen: string }) => void;
  'agents_online_status_update': (data: { onlineAgents: Array<{ agentId: string; isOnline: boolean; lastSeen: string; agentName: string }> }) => void;
  'ai_status_changed': (data: { ticketId: string; enabled: boolean; reason?: string; changedBy: string }) => void;
  'ticket_summary_generated': (data: { ticketId: string; summary: string; generatedAt: string; modelVersion: string; confidence: number }) => void;
  'ticket_details_updated': (data: { ticketId: string; title: string; description: string; confidence: number; generatedAt: string }) => void;
  'company_match_suggestion': (data: { pendingMatchId: string; ticketId: string; ticketNumber: string; customerName: string; inputCompanyName: string; suggestedCompany: string; confidence: number; message: string }) => void;
  'workflow_updated': (data: { ticketId: string; workflowId: string; workflow: any; updatedStep: any; updatedBy: any; updatedAt: string }) => void;
  'ticket_converted_to_email': (data: { ticketId: string; reason: string; timestamp: string }) => void;
  // Agent notification events
  'agent_notification': (data: { 
    type: 'ticket_assignment' | 'customer_message' | 'agent_status' | 'unassigned_ticket' | 'escalation' | 'system' | 'service_report_approval';
    priority: 'low' | 'medium' | 'high' | 'urgent';
    title: string;
    message: string;
    actionButton?: {
      text: string;
      action: 'open_ticket' | 'view_ticket' | 'view_agent' | 'custom' | 'approve_report' | 'view_workflow';
      data?: any;
    };
    sound?: boolean;
    data?: any;
  }) => void;
}

class SocketService {
  private socket: Socket | null = null;
  private isConnected = false;
  private currentTicketId: string | null = null;
  private baseURL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
  private claimedTicketRooms: Set<string> = new Set();

  constructor() {
    // Socket.IO is now available via the installed package
    console.log('🔌 SocketService initialized with backend URL:', this.baseURL);
  }

  connect(token?: string): Promise<void> {
    if (this.socket?.connected) {
      return Promise.resolve();
    }

    console.log('🔌 Connecting to Socket.IO server...');

    return new Promise((resolve, reject) => {
      this.socket = io(this.baseURL, {
        auth: {
          token: token || ''
        },
        transports: ['websocket', 'polling'],
        timeout: 10000,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000
      });

      this.socket.on('connect', () => {
        console.log('✅ Socket.IO connected:', this.socket?.id);
        this.isConnected = true;
        resolve();
      });

      this.socket.on('disconnect', (reason) => {
        console.log('❌ Socket.IO disconnected:', reason);
        this.isConnected = false;
        
        // Don't treat normal disconnections as errors
        if (reason === 'io server disconnect' || reason === 'io client disconnect') {
          console.log('ℹ️ Socket disconnected normally');
        }
      });

      this.socket.on('connect_error', (error) => {
        console.error('❌ Socket.IO connection error:', error);
        this.isConnected = false;
        
        // Don't reject immediately on connection error, let it retry
        console.log('🔄 Socket.IO will attempt to reconnect...');
      });

      this.socket.on('reconnect', (attemptNumber) => {
        console.log('✅ Socket.IO reconnected after', attemptNumber, 'attempts');
        this.isConnected = true;
        resolve();
      });

      this.socket.on('reconnect_error', (error) => {
        console.error('❌ Socket.IO reconnection error:', error);
      });

      this.socket.on('reconnect_failed', () => {
        console.error('❌ Socket.IO reconnection failed completely');
        this.isConnected = false;
        reject(new Error('Socket.IO reconnection failed'));
      });

      // Add timeout to prevent hanging
      setTimeout(() => {
        if (!this.isConnected) {
          console.warn('⚠️ Socket connection timeout after 10 seconds');
          // Don't reject here, let reconnection handle it
        }
      }, 10000);
    });
  }

  disconnect(): void {
    if (this.socket) {
      console.log('🔌 Disconnecting from Socket.IO server...');
      
      // Leave current ticket room before disconnecting
      if (this.currentTicketId) {
        this.leaveTicket(this.currentTicketId);
      }
      
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
      this.currentTicketId = null;
    }
  }

  // Force disconnect without leaving ticket room (for browser close scenarios)
  forceDisconnect(): void {
    if (this.socket) {
      console.log('🔌 Force disconnecting from Socket.IO server...');
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
      this.currentTicketId = null;
    }
  }

  // Join a ticket room for real-time updates
  joinTicket(ticketId: string, isCustomer: boolean = false): void {
    if (!this.socket || !this.isConnected) {
      console.warn('⚠️ Socket not connected, cannot join ticket room');
      return;
    }

    // For customers, leave previous room before joining new one
    if (isCustomer && this.currentTicketId) {
      console.log('🚪 Customer leaving previous ticket room:', this.currentTicketId);
      this.leaveTicket(this.currentTicketId);
    }

    console.log('📝 Joining ticket room:', ticketId, isCustomer ? '(as customer)' : '(as agent viewing)');
    
    // Always join the ticket room for real-time updates
    // Backend can handle duplicate room joins safely
    console.log('📝 Emitting join_ticket event for:', ticketId);
    this.socket.emit('join_ticket', { ticketId, isCustomer });
    
    // Track the currently active ticket for UI purposes
    this.currentTicketId = ticketId;
  }

  // Join claimed ticket rooms (agents stay in multiple rooms for red indicators)
  joinClaimedTicketRooms(ticketIds: string[]): void {
    if (!this.socket || !this.isConnected) {
      console.warn('⚠️ Socket not connected, cannot join claimed ticket rooms');
      return;
    }

    console.log('📝 Joining claimed ticket rooms:', ticketIds);
    
    ticketIds.forEach(ticketId => {
      if (!this.claimedTicketRooms.has(ticketId)) {
        console.log('📝 Joining claimed ticket room:', ticketId);
        if (this.socket) {
          this.socket.emit('join_ticket', { ticketId, isCustomer: false });
        }
        this.claimedTicketRooms.add(ticketId);
      }
    });
    
    console.log('📝 Agent now in claimed rooms:', Array.from(this.claimedTicketRooms));
  }

  // Leave claimed ticket room when ticket is unclaimed
  leaveClaimedTicketRoom(ticketId: string): void {
    if (!this.socket || !this.isConnected) {
      console.warn('⚠️ Socket not connected, cannot leave claimed ticket room');
      return;
    }

    if (this.claimedTicketRooms.has(ticketId)) {
      console.log('📝 Leaving claimed ticket room:', ticketId);
      if (this.socket) {
        this.socket.emit('leave_ticket', { ticketId });
      }
      this.claimedTicketRooms.delete(ticketId);
    }
  }

  // Clear all claimed ticket rooms (on logout)
  clearClaimedTicketRooms(): void {
    console.log('📝 Clearing all claimed ticket rooms');
    this.claimedTicketRooms.forEach(ticketId => {
      this.leaveClaimedTicketRoom(ticketId);
    });
    this.claimedTicketRooms.clear();
  }

  // Leave current ticket room
  leaveTicket(ticketId: string): void {
    if (!this.socket || !this.isConnected) {
      return;
    }

    console.log('🚪 Leaving ticket room:', ticketId);
    
    // For agents: Don't leave if this is a claimed ticket room (they need to stay for red indicators)
    // For customers: Always leave the room
    if (this.claimedTicketRooms.has(ticketId)) {
      console.log('🏢 Not leaving claimed ticket room (agent needs to stay for red indicators):', ticketId);
      // Don't emit leave_ticket for claimed rooms
    } else {
      console.log('🚪 Leaving non-claimed ticket room:', ticketId);
      this.socket.emit('leave_ticket', { ticketId });
    }
    
    if (this.currentTicketId === ticketId) {
      this.currentTicketId = null;
    }
  }

  // Send a message
  sendMessage(ticketId: string, content: string): void {
    if (!this.socket || !this.isConnected) {
      console.warn('⚠️ Socket not connected, cannot send message');
      return;
    }

    this.socket.emit('send_message', { ticketId, content });
  }

  // Typing indicators
  startTyping(ticketId: string): void {
    if (!this.socket || !this.isConnected) {
      return;
    }

    this.socket.emit('typing_start', { ticketId });
  }

  stopTyping(ticketId: string): void {
    if (!this.socket || !this.isConnected) {
      return;
    }

    this.socket.emit('typing_stop', { ticketId });
  }

  // Agent dashboard presence
  joinAgentDashboard(agentId: string, agentName: string): void {
    console.log('🔧 joinAgentDashboard called with:', { agentId, agentName });
    console.log('🔧 Socket status:', { 
      socketExists: !!this.socket, 
      isConnected: this.isConnected, 
      actuallyConnected: this.socket?.connected 
    });
    
    if (!this.socket || !this.isConnected) {
      console.warn('⚠️ Socket not connected, cannot join agent dashboard');
      console.warn('⚠️ Socket details:', { 
        socketExists: !!this.socket, 
        isConnected: this.isConnected,
        actuallyConnected: this.socket?.connected
      });
      
      // Retry after a shorter delay for faster connection
      setTimeout(() => {
        console.log('🔄 Retrying joinAgentDashboard after delay...');
        this.joinAgentDashboard(agentId, agentName);
      }, 200);
      return;
    }

    console.log('👨‍💼 Emitting agent_dashboard_join event:', { agentId, agentName });
    this.socket.emit('agent_dashboard_join', { agentId, agentName });
    console.log('✅ agent_dashboard_join event emitted successfully');
  }

  leaveAgentDashboard(agentId: string, agentName: string): void {
    if (!this.socket || !this.isConnected) {
      return;
    }

    console.log('👨‍💼 Leaving agent dashboard:', { agentId, agentName });
    this.socket.emit('agent_dashboard_leave', { agentId, agentName });
  }

  // Get current agent status
  getAgentStatus(): Promise<any> {
    return new Promise((resolve, reject) => {
      if (!this.socket || !this.isConnected) {
        reject(new Error('Socket not connected'));
        return;
      }

      // Request current agent status
      this.socket.emit('get_agent_status');
      
      // Listen for response
      this.socket.once('agent_status_response', (data) => {
        resolve(data);
      });

      // Timeout after 5 seconds
      setTimeout(() => {
        reject(new Error('Agent status request timeout'));
      }, 5000);
    });
  }

  // Force refresh agent status
  refreshAgentStatus(): void {
    if (!this.socket || !this.isConnected) {
      return;
    }

    console.log('🔄 Requesting agent status refresh...');
    this.socket.emit('refresh_agent_status');
  }

  // Event listeners
  on<K extends keyof SocketEvents>(event: K, callback: SocketEvents[K]): void {
    if (!this.socket) {
      return;
    }

    this.socket.on(event as string, callback);
  }

  off<K extends keyof SocketEvents>(event: K, callback?: SocketEvents[K]): void {
    if (!this.socket) {
      return;
    }

    if (callback) {
      this.socket.off(event as string, callback);
    } else {
      this.socket.off(event as string);
    }
  }

  // Utility methods
  isSocketConnected(): boolean {
    return this.isConnected && this.socket?.connected === true;
  }

  getCurrentTicketId(): string | null {
    return this.currentTicketId;
  }
  
  // Debugging methods
  getClaimedTicketRooms(): Set<string> {
    return this.claimedTicketRooms;
  }
  
  getSocket(): Socket | null {
    return this.socket;
  }
}

export const socketService = new SocketService();

// Make socketService available globally for debugging
if (typeof window !== 'undefined') {
  (window as any).socketService = socketService;
}

export default socketService; 